/**
 * RoomExecutionRow — one execution as a TABLE ROW for the Rooms-landing inline overview.
 *
 * Carries the SAME columns as the room's full ExecutionsTab (Status · Trigger · User ·
 * Prompt · Task done · Started · Duration). Rendered as a ``<tr>`` inside the grid's
 * table (which owns the ``<thead>``). The one deliberate difference: the User cell
 * resolves the triggering user's sub (a long UUID) to a display name/email via
 * ``resolveUser`` (a sub→member map the grid builds from the pool roster), so runs
 * read "Jane Doe", not a UUID.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';
import { formatRelativeTime } from '@stepflow/ui-shared/utils/formatDate.js';
import { durationBetween } from '@stepflow/ui-shared/utils/formatDuration.js';
import {
  bucketStatus, statusPillClassName, EXECUTION_STATUS_BUCKETS, isLiveBucket,
} from '@stepflow/ui-shared/utils/executionStatus.js';

const TRIGGER_STYLE = {
  UI: 'bg-blue-50 text-blue-700 ring-blue-200',
  API: 'bg-violet-50 text-violet-700 ring-violet-200',
  JOB: 'bg-amber-50 text-amber-700 ring-amber-200',
};

// Trigger badge — HOW the run started (UI | API | JOB); JOB shows the loop episode.
function TriggerBadge({ type, episode }) {
  const t = String(type || '').trim().toUpperCase();
  if (!t) return <span className="text-gray-300">—</span>;
  const cls = TRIGGER_STYLE[t] || 'bg-slate-100 text-slate-600 ring-slate-200';
  const epNum = (episode != null && Number.isFinite(Number(episode))) ? Number(episode) + 1 : null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`rounded px-1 py-px text-[9px] font-semibold ring-1 ring-inset ${cls}`}>{t}</span>
      {t === 'JOB' && epNum ? <span className="text-[9px] text-gray-400">ep {epNum}</span> : null}
    </span>
  );
}

// Cognito subs look like ``14a8d418-20a1-7077-b0cd-…`` (UUID-shaped). Treat those as
// NOT a human-readable name so they route through the roster resolver instead of
// being shown raw (the bug this fixes).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function looksLikeSub(s) {
  return UUID_RE.test(String(s || '').trim());
}

// Shorten a bare sub UUID for the last-ditch fallback (no email, not in roster).
function shortenSub(s) {
  const v = String(s || '').trim();
  if (v.length <= 12) return v;
  return `${v.slice(0, 6)}…${v.slice(-4)}`;
}

export default function RoomExecutionRow({ row, resolveUser }) {
  const r = row || {};
  const bucket = bucketStatus(r.status);
  const meta = EXECUTION_STATUS_BUCKETS[bucket] || EXECUTION_STATUS_BUCKETS.unknown;
  const live = isLiveBucket(bucket);
  const finishedAt = !live ? (r.endedAt || r.updatedAt || r.completedAt) : undefined;
  const duration = durationBetween(r.createdAt, finishedAt);
  const prompt = String(r.prompt || '').trim();

  // WHO triggered it. Prefer a HUMAN-READABLE explicit field (actorEmail, or a
  // username that isn't itself a sub). Otherwise resolve the sub against the pool
  // roster → name/email. Finally shorten the UUID so a stranger's run (not in the
  // roster) at least isn't a 36-char blob. A UUID-shaped username/triggeredBy is
  // NOT treated as a name — that was the bug (the raw sub leaked through).
  const rawUsername = String(r.username || '').trim();
  const explicit = String(r.actorEmail || '').trim()
    || (rawUsername && !looksLikeSub(rawUsername) ? rawUsername : '');
  // The sub to resolve: the row's triggeredBy, or a UUID-shaped username.
  const sub = String(r.triggeredBy || '').trim()
    || (looksLikeSub(rawUsername) ? rawUsername : '');
  const resolved = !explicit && sub && resolveUser ? resolveUser(sub) : null;
  const who = explicit || resolved || (sub ? shortenSub(sub) : '');
  const whoTitle = explicit || (resolved ? `${resolved}${sub ? ` · ${sub}` : ''}` : sub);

  const label = String(r.taskLabel || '').trim();
  const summary = String(r.taskSummary || '').trim();

  return (
    <tr
      className="align-top hover:bg-gray-50/60"
      data-testid={`room-exec-row-${r.taskId}`}
    >
      {/* Status */}
      <td className="whitespace-nowrap px-2 py-1.5">
        <span className={statusPillClassName(bucket)}>
          {live ? <Loader2 className="mr-0.5 h-2.5 w-2.5 animate-spin" aria-hidden /> : null}
          {meta.label}
        </span>
      </td>
      {/* Trigger */}
      <td className="whitespace-nowrap px-2 py-1.5">
        <TriggerBadge type={r.triggerType} episode={r.loopIteration} />
      </td>
      {/* User (resolved sub → name) — fills its column (width set by the table colgroup). */}
      <td className="px-2 py-1.5">
        <span className="block truncate text-[10px] text-gray-600" title={whoTitle || undefined}>
          {who || <span className="text-gray-300">—</span>}
        </span>
      </td>
      {/* Prompt — single-line truncate against the wide column; full text on hover. */}
      <td className="px-2 py-1.5">
        <span className="block truncate text-[11px] text-gray-700" title={prompt || undefined}>
          {prompt || <span className="text-gray-400">(no prompt)</span>}
        </span>
      </td>
      {/* Task done (label + summary) — the widest column; summary wraps up to 2 lines
          so the run's outcome is readable inline (keeps the row height compact). */}
      <td className="px-2 py-1.5">
        {(label || summary) ? (
          <span className="block" title={[label, summary].filter(Boolean).join('\n')}>
            {label ? <span className="block truncate text-[10px] font-medium text-gray-600">{label}</span> : null}
            {summary ? <span className="line-clamp-2 text-[10px] leading-snug text-gray-400">{summary}</span> : null}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      {/* Started */}
      <td className="whitespace-nowrap px-2 py-1.5 text-right text-[10px] text-gray-400"
          title={r.createdAt || undefined}>
        {formatRelativeTime(r.createdAt)}
      </td>
      {/* Duration */}
      <td className="whitespace-nowrap px-2 py-1.5 text-right font-mono text-[10px] text-gray-500">
        {duration && duration !== '—' ? duration : <span className="text-gray-300">—</span>}
      </td>
    </tr>
  );
}
